// DiagnoseView.tsx - the locked dx5 Diagnose surface (S21). You read the villain yourself, weight his
// hands, LOCK the read (Axis B), then play the street: a multiple-choice action that never auto-fills
// dollars, and a manual bet-math builder when you raise. The app shows the price with a fading math aid.
//
// On commit, the inline two-axis grade reveal (S22) drops into the decision seam: the verdict, Axis A
// (the play) + Axis B (the read) in the Chess.com move-class hexes, wrong-answer coaching on the street
// you diverged, and the "See the full debrief" Coach Drawer.
//
// After the river, the end-of-hand report (S23) closes the hand: every committed decision is accumulated
// across the four streets (`results`) and projected into <EndOfHandReport> - a real accuracy line, band
// counts, and street rows, NOT a fixture. Deep-linkable at ?mode=diagnose&street=0..3 and
// ?mode=diagnose&view=report (a bare report deep-link fills all four streets with the correct line).
//
// Wired to the engine on two seams: the felt comes from S19's engine bridge (teachingFeltView, same
// A-J-vs-Vacuum teaching hand as Observe), and the lock/revise lifecycle runs @postflop/engine's
// diagnosis lockReducer (revision timing captured, never scored - North Star §5).
import { useEffect, useMemo, useRef, useState } from 'react';
import { initialLock, lockReducer } from '@postflop/engine';
import type { ReadLock } from '@postflop/schema';
import { OvalFelt } from '../components/OvalFelt';
import { StreetStepper, STREET_LABELS } from '../components/StreetStepper';
import { teachingFeltView } from '../engine/bridge';
import { DIAGNOSE_STREETS, type ActionDef, type PlayerTag } from './data';
import { ReadLockIn } from './ReadLockIn';
import { MathAid, type AidLevel } from './MathAid';
import { ActionRamp } from './ActionRamp';
import { GradeReveal } from '../grade/GradeReveal';
import { resolveGrade, canonicalGrade } from '../grade/resolve';
import type { ResolvedGrade } from '../grade/types';
import { EndOfHandReport } from '../report/EndOfHandReport';
import { buildDiagnoseReport } from '../report/report';
import { persistPlayedHand } from '../progress/persistPlay';

const LAST = DIAGNOSE_STREETS.length - 1;

type View = 'stepper' | 'report';

/** Read the initial view from ?view=report. */
function initialView(): View {
  if (typeof window === 'undefined') return 'stepper';
  return new URLSearchParams(window.location.search).get('view') === 'report' ? 'report' : 'stepper';
}

/** Read the initial street from ?street=0..3 (clamped); default preflop. A report deep-link lands on the
 *  river so stepping Back from the report shows the last decision. */
function initialStreet(): number {
  if (typeof window === 'undefined') return 0;
  if (initialView() === 'report') return LAST;
  const raw = Number(new URLSearchParams(window.location.search).get('street'));
  return Number.isInteger(raw) && raw >= 0 && raw <= LAST ? raw : 0;
}

/** Keep the URL in sync so a street - and the report - is deep-linkable and shareable. */
function syncUrl(street: number, view: View) {
  if (typeof window === 'undefined') return;
  const q = new URLSearchParams();
  q.set('mode', 'diagnose');
  q.set('street', String(street));
  if (view === 'report') q.set('view', 'report');
  window.history.replaceState(null, '', '?' + q.toString());
}

type Phase = 'read' | 'graded';

export function DiagnoseView() {
  const [view, setView] = useState<View>(initialView);
  const [street, setStreet] = useState(initialStreet);
  const data = DIAGNOSE_STREETS[street];
  const felt = useMemo(() => teachingFeltView(street), [street]);
  const isRiver = street === LAST;

  // per-street read state. Reset whenever the street changes (a fresh read each street).
  const [tag, setTag] = useState<PlayerTag | null>(null);
  const [weights, setWeights] = useState<number[]>([]);
  // the read's lock lifecycle. Transitions run through @postflop/engine's lockReducer; a fresh `open`
  // lock is set on every street change (the reducer has no reset event by design).
  const [lock, setLock] = useState<ReadLock>(initialLock);
  const [phase, setPhase] = useState<Phase>('read');
  const [grade, setGrade] = useState<ResolvedGrade | null>(null);
  // the accumulated per-street grades - the spine of the end-of-hand report. Persists across street
  // navigation (NOT cleared by the per-street reset); each commit overwrites its street.
  const [results, setResults] = useState<(ResolvedGrade | null)[]>(() =>
    DIAGNOSE_STREETS.map(() => null),
  );
  // how much the math aid explains. A user preference that persists across streets (the eventual
  // arithmetic-accuracy auto-fade is deferred - see <MathAid>).
  const [aidLevel, setAidLevel] = useState<AidLevel>('full');

  const locked = lock.state === 'lockedIn';

  // reset the read on every street change (and on first mount for the deep-linked street).
  useEffect(() => {
    setTag(null);
    setWeights(data.families ? data.families.map(() => 0) : []);
    setLock(initialLock()); // a fresh, un-committed read each street
    setPhase('read');
    setGrade(null);
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [street]);

  // keep the URL pointed at the current street/view.
  useEffect(() => syncUrl(street, view), [street, view]);

  const go = (i: number) => setStreet(Math.max(0, Math.min(LAST, i)));

  const commit = (key: ActionDef['key'], amount?: string) => {
    const resolved = resolveGrade(street, key, tag, amount);
    setGrade(resolved);
    setResults((r) => {
      const next = r.slice();
      next[street] = resolved;
      return next;
    });
    setPhase('graded');
  };

  // Fire the D1 persistence exactly once per played hand (Phase 2 "real progress"). Best-effort: the
  // report never waits on it, and an offline / Worker-down failure degrades silently (the Progress tab
  // just won't reflect this hand). Reset by playAgain so the next hand logs its own decisions.
  const persistedRef = useRef(false);

  const openReport = () => {
    setView('report');
    window.scrollTo({ top: 0 });
    if (!persistedRef.current) {
      persistedRef.current = true;
      // Map each street's two-axis reveal into the surface-neutral shape: verdict = the decision grade,
      // the play (A) and read (B) become the two skill dimensions.
      void persistPlayedHand(
        results.map((g) =>
          g
            ? {
                gradeClass: g.verdict.cls,
                skillClasses: { value_extraction: g.axisA.cls, player_profiling: g.axisB.cls },
              }
            : null,
        ),
        'guided',
      ).then((r) => {
        if (!r.ok) console.warn('[progress] persist failed:', r.error);
      });
    }
  };

  const playAgain = () => {
    persistedRef.current = false;
    setResults(DIAGNOSE_STREETS.map(() => null));
    setView('stepper');
    setStreet(0); // triggers the reset effect (phase -> read)
    window.scrollTo({ top: 0 });
  };

  // the report reads four REAL grades: what the user played on each street, or the correct line for any
  // street not played through (a bare ?view=report deep-link, or jumping straight to the report).
  const reportModel = useMemo(
    () => buildDiagnoseReport(DIAGNOSE_STREETS.map((_, i) => results[i] ?? canonicalGrade(i))),
    [results],
  );

  if (view === 'report') {
    return (
      <>
        <div className="body">
          <EndOfHandReport model={reportModel} />
        </div>
        <div className="dock">
          <div className="dockrow">
            <button
              type="button"
              className="cta ghost prev"
              onClick={() => {
                setView('stepper');
                window.scrollTo({ top: 0 });
              }}
            >
              Back
            </button>
            <button type="button" className="cta" onClick={playAgain}>
              Play another hand
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OvalFelt view={felt} />

      <div className="body">
        <div>
          <div className="kicker">{data.kicker}</div>
          <div className="h2">{data.h2}</div>
          <div className="lede">{data.lede}</div>
        </div>

        <StreetStepper street={street} onSelect={go} />

        {phase === 'read' ? (
          <>
            <ReadLockIn
              tagSub={data.tagSub}
              families={data.families}
              tag={tag}
              weights={weights}
              locked={locked}
              onTag={setTag}
              onCycleWeight={(i) =>
                setWeights((w) => w.map((v, j) => (j === i ? (v + 1) % 4 : v)))
              }
              onLock={() => setLock((l) => lockReducer(l, { type: 'lockIn', atDecisionIndex: street }))}
              onRevise={() => setLock((l) => lockReducer(l, { type: 'edit' }))}
            />
            <MathAid price={data.price} level={aidLevel} onLevel={setAidLevel} />
          </>
        ) : (
          grade && <GradeReveal grade={grade} />
        )}
      </div>

      <div className="dock">
        {phase === 'read' ? (
          <ActionRamp actions={data.actions} bet={data.bet} locked={locked} onCommit={commit} />
        ) : (
          <div className="dockrow">
            <button
              type="button"
              className="cta ghost prev"
              disabled={street === 0}
              onClick={() => go(street - 1)}
            >
              Back
            </button>
            {/* river closes into the end-of-hand report (S23); earlier streets step to the next read. */}
            {isRiver ? (
              <button type="button" className="cta" onClick={openReport}>
                See the hand report
              </button>
            ) : (
              <button type="button" className="cta" onClick={() => go(street + 1)}>
                Next: the {STREET_LABELS[street + 1].toLowerCase()}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
